import {
  FavoritesFragment,
  GetFavoritesQuery,
  UpdateFavoriteListMutation,
} from '../graphql/product/generated/graphql-types'
import { use, useCallback } from 'react'
import { getUpdatedFavoritesCache } from './cache-updater-favorites'
import {
  addToFavoritesAction,
  addToFavoritesActionWithoutForm,
  deleteFavoriteListAction,
  getFavorites,
} from '../actions/add-to-favorite-action'
import { useSWR } from './use-graphql-swr'
import { getCacheKeyFromDocument } from './graphql-fetcher'
import { notifyFavoriteAction } from '../actions/notify-favorite-click-action'
import { GET_FAVORITES } from '../graphql/product/get-customer-product-lists.graphql'
import { Middleware,  SWRHook, useSWRConfig } from 'swr'
export type UpdateFavorites = ReturnType<typeof useFavoritesSwr>['updateFavorites']
export type FavoriteProductList = NonNullable<FavoritesFragment['productLists']>

type FavoriteItemInfo = {
  productId: string
  quantity: number
  displayName: string
}

type FavoriteListInfo = {
  favoriteListName: string
  favoriteListId?: number
}

type Props = FavoriteItemInfo & (FavoriteListInfo | { formData: FormData })

const resolveReactPromise: Middleware = (useSWRNext: SWRHook) => (key, fetcher, config) => {
  const { fallback } = useSWRConfig()
  const cacheKey = getCacheKeyFromDocument(GET_FAVORITES)
  const cachedData = fallback[cacheKey] as
    | GetFavoritesQuery
    | Promise<GetFavoritesQuery>
    | undefined
  if (typeof cachedData === 'object' && cachedData instanceof Promise) {
    const favorites = use(cachedData)
    return useSWRNext(key, fetcher, { ...config, fallbackData: favorites as any })
  }
  return useSWRNext(key, fetcher, config)
}

export const useFavoritesSwr = () => {
  const {
    data: favoritesData,
    error,
    isValidating: loading,
    mutate,
  } = useSWR<GetFavoritesQuery>(getCacheKeyFromDocument(GET_FAVORITES), getFavorites, {
    revalidateOnMount: false,
    use: [resolveReactPromise],
  })

  const notifyFavoriteClick = async (event: 'add' | 'remove', productErpId: string) => {
    await notifyFavoriteAction(event, { variables: { productErpIdentifier: productErpId } })
  }

  const updateFavorites = useCallback(
    (props: Props) => {
      const { productId, displayName, quantity } = props

      const favoriteListName =
        'favoriteListName' in props
          ? props.favoriteListName
          : props.formData.get('favouriteListName')?.toString()

      if (!favoriteListName) {
        throw new Error('Favorite list name is required')
      }

      return mutate(
        async () => {
          let newData: UpdateFavoriteListMutation

          if ('formData' in props) {
            // props.formData
            newData = await addToFavoritesAction(
              {
                productErpIdentifier: productId,
                quantity,
              },
              props.formData
            )
          } else {
            const { favoriteListId } = props

            newData = await addToFavoritesActionWithoutForm({
              listName: favoriteListName,
              productErpIdentifier: productId,
              quantity,
              listId: favoriteListId,
            })
          }

          const updatedProductList = getUpdatedFavoritesCache({
            productId,
            displayName,
            quantity,
            favoriteListName,
            newData,
            favoritesData: favoritesData as GetFavoritesQuery,
          })

          const updatedData = {
            favorites: { __typename: 'Favorites', productLists: updatedProductList },
          }

          return updatedData as GetFavoritesQuery
        },
        {
          rollbackOnError: true,
          revalidate: false,
        }
      )
    },
    [favoritesData, mutate]
  )

  const deleteFavoriteList = useCallback(
    (id: number) => {
      mutate(
        async () => {
          const deletedData = await deleteFavoriteListAction(id)

          const cachedFavoritesLists = favoritesData?.favorites?.productLists ?? []
          const updatedData = {
            favorites: {
              __typename: 'Favorites',
              productLists: cachedFavoritesLists?.filter(
                list => list?.id !== deletedData?.deleteFavoriteList?.id
              ),
            },
          }

          return updatedData
        },
        {
          rollbackOnError: true,
          revalidate: false,
        }
      )
    },
    [favoritesData?.favorites?.productLists, mutate]
  )

  if (error) {
    throw error
  }

  return {
    loading,
    error,
    favoritesLists: favoritesData?.favorites?.productLists ?? [],
    updateFavorites,
    deleteFavoriteList,
    notifyFavoriteClick,
    refetchFavorites: mutate,
  }
}
